import { useCallback, useEffect, useState } from "react";
import { Check, Loader2, ShieldAlert, X } from "lucide-react";
import { api, apiError, shortDate } from "@/lib/api";
import { ListState, PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const TABS = ["Pending", "Approved", "Rejected", "Completed"];

export default function Approvals() {
  const [tab, setTab] = useState("Pending");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/approvals/queue?status=${tab}`);
      setData(d);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const decide = async (row, action) => {
    setBusy(row.id);
    try {
      if (row.source === "customer_edit") {
        await api.post(`/customer-admin/pending-edits/${row.record_id}/${action}`,
          action === "reject" ? { reason: "Not approved" } : {});
      } else if (row.source === "ai_request") {
        await api.post(`/ai/requests/${row.id}/decision`,
          { decision: action === "approve" ? "approved" : "rejected" });
      } else {
        await api.post(`/approvals/requests/${row.id}/${action}`, {});
      }
      toast.success(action === "approve" ? "Approved and applied" : "Rejected — nothing changed");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy("");
    }
  };

  return (
    <div data-testid="approval-center">
      <PageHeader
        testId="approval-center-header"
        title="Approval Center"
        subtitle="Every edit and delete request across the ERP, in one place."
      />
      <div className="p-4 sm:p-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList data-testid="approval-tabs">
            {TABS.map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`approval-tab-${t.toLowerCase()}`}>
                {t}{data?.counts?.[t] ? ` (${data.counts[t]})` : ""}
              </TabsTrigger>
            ))}
          </TabsList>
          {TABS.map((t) => (
            <TabsContent key={t} value={t}>
              <ListState loading={loading} isEmpty={!data || data.rows.length === 0}
                emptyTitle={`Nothing ${t.toLowerCase()}`}
                emptyHint="Staff requests to edit or delete records appear here.">
                <div className="grid gap-3">
                  {(data?.rows || []).map((r) => (
                    <div key={`${r.source}-${r.id}`} className="surface p-4"
                      data-testid={`approval-card-${r.id}`}>
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <div className="flex flex-wrap items-center gap-1.5">
                            <Badge variant="outline" className="text-[10px]">
                              {r.module_label || r.module}
                            </Badge>
                            <Badge variant={r.action?.includes("cancel") || r.action === "delete"
                              ? "destructive" : "secondary"}
                              className="text-[10px]">{r.action_label || r.action}</Badge>
                            <span className="text-sm font-medium">
                              {r.record_name || r.document_number || "—"}
                            </span>
                          </div>
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            asked by {r.requested_by || "staff"} • {shortDate(r.requested_at)}
                            {r.reason ? ` • ${r.reason}` : ""}
                          </div>
                        </div>
                        {data?.can_approve && t === "Pending" && (
                          <div className="flex gap-2">
                            <Button size="sm" disabled={busy === r.id}
                              data-testid={`approve-${r.id}`}
                              onClick={() => decide(r, "approve")}>
                              <Check className="mr-1 h-4 w-4" /> Approve
                            </Button>
                            <Button size="sm" variant="outline" disabled={busy === r.id}
                              data-testid={`reject-${r.id}`}
                              onClick={() => decide(r, "reject")}>
                              <X className="mr-1 h-4 w-4" /> Reject
                            </Button>
                          </div>
                        )}
                      </div>

                      {(r.changes || []).length > 0 && (
                        <div className="mt-2 grid gap-1" data-testid={`approval-diff-${r.id}`}>
                          {r.changes.map((c) => (
                            <div key={c.field} className="flex flex-wrap gap-2 text-xs">
                              <span className="w-36 text-muted-foreground">
                                {c.label || c.field}
                              </span>
                              <span className="text-muted-foreground line-through">
                                {String(c.current || "—")}
                              </span>
                              <span className="font-medium">→ {String(c.proposed || "—")}</span>
                            </div>
                          ))}
                        </div>
                      )}

                      {Object.keys(r.impact || {}).length > 0 && (
                        <div className="mt-2 flex items-center gap-1.5 rounded border border-amber-500/40 bg-amber-500/10 p-2 text-xs"
                          data-testid={`approval-impact-${r.id}`}>
                          <ShieldAlert className="h-3.5 w-3.5" />
                          Linked: {Object.entries(r.impact).map(([k, v]) => `${v} ${k}`)
                            .join(", ")} — approving a delete will archive, never erase.
                        </div>
                      )}

                      {r.decided_by && (
                        <div className="mt-2 text-xs text-muted-foreground">
                          decided by {r.decided_by}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </ListState>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </div>
  );
}
